import React, { useState, useRef } from 'react';
import { Camera } from 'lucide-react';
import { storageService } from '../../../services/storageService';
import { userProfileService } from '../../../services/userProfileService';

const UserAvatarUpload = ({ userId, currentAvatarUrl, onAvatarUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentAvatarUrl);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes?.includes(file?.type)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (2MB)
    if (file?.size > 2097152) {
      alert('File size must be less than 2MB');
      return;
    }

    setUploading(true);

    try {
      // Upload to storage
      const { data: filePath, error: uploadError } = await storageService?.uploadUserAvatar(file, userId);
      
      if (uploadError) throw uploadError;

      // Update user profile with new avatar path
      const { error: updateError } = await userProfileService?.updateUserAvatar(userId, filePath);
      
      if (updateError) throw updateError;

      // Get signed URL for preview
      const { data: signedUrl } = await storageService?.getUserAvatarUrl(filePath);
      
      if (signedUrl) {
        setPreviewUrl(signedUrl);
        onAvatarUpdate?.(signedUrl);
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload avatar. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="relative group">
      <div 
        className="w-12 h-12 rounded-xl overflow-hidden cursor-pointer transition-all hover:opacity-80"
        onClick={handleClick}
        style={{ 
          background: previewUrl ? 'transparent' : 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)'
        }}
      >
        {previewUrl ? (
          <img 
            src={previewUrl} 
            alt="User avatar" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Camera className="w-6 h-6 text-white" />
          </div>
        )}
      </div>
      
      {uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
        <Camera className="w-3 h-3 text-white" />
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default UserAvatarUpload;